/**
 * Breathing protocols for apnea training.
 *
 * NAUI-sourced protocols are transcribed from the NAUI Freediver Student Manual
 * (2021), "Proper Breathing & Stress Reduction", pp.34-35. Where the manual and
 * other training material disagree, the manual wins and the divergence is noted
 * on the protocol itself.
 *
 * Timings drive the guided breathing metronome in the session runner, so they are
 * stored as seconds rather than display strings.
 */

export type ProtocolSource = 'naui' | 'supplementary';

/** One step in a breathing pattern. A null duration means the user ends it. */
export interface BreathStep {
  action: 'inhale' | 'exhale' | 'hold' | 'holdEmpty';
  seconds: number | null;
  /** Shown during this step in the runner. Keep to a few words — read at a glance. */
  cue: string;
}

export interface BreathingProtocol {
  id: string;
  name: string;
  source: ProtocolSource;
  /** One line on what this is for. */
  purpose: string;
  steps: BreathStep[];
  /** Repetitions, when the protocol prescribes a count. */
  repetitions?: { min: number; max: number };
  /** Total duration guidance where reps are open-ended, in seconds. */
  durationSeconds?: { min: number; max: number };
  /** Safety text that must be shown wherever this protocol is offered. */
  caution?: string;
  citation: string;
}

/**
 * NAUI's core pre-dive breathing pattern: "the meat-and-potatoes of the
 * breathing cycle". Inhale is deliberately quick to avoid raising heart rate;
 * the long exhale is the point.
 */
export const VENTILATION_BREATHING: BreathingProtocol = {
  id: 'ventilation',
  name: 'Ventilation breathing',
  source: 'naui',
  purpose: 'Maximise gas exchange and tissue oxygen saturation before a hold.',
  steps: [
    { action: 'inhale', seconds: 2, cue: 'Inhale — diaphragm, then ribs' },
    { action: 'hold', seconds: 2, cue: 'Hold' },
    { action: 'exhale', seconds: 12, cue: 'Long, controlled exhale' },
  ],
  caution:
    'Inhale to the largest comfortable volume without involving the shoulders. '
    + 'Keep the exhale slow and regulated — heart rate falls on exhalation.',
  citation: 'NAUI Freediver Manual (2021) p.35',
};

/**
 * The manual gives the exhale as a 10-15 second range; the protocol above uses
 * the midpoint. Exposed so the UI can offer the range rather than hard-coding it.
 */
export const VENTILATION_EXHALE_RANGE_SECONDS = { min: 10, max: 15 };

/**
 * Normal (diaphragmatic) breathing — the resting baseline, not a timed pattern.
 */
export const NORMAL_BREATHING: BreathingProtocol = {
  id: 'normal',
  name: 'Normal breathing',
  source: 'naui',
  purpose: 'Everyday diaphragmatic breathing; the lowest-effort resting state.',
  steps: [
    { action: 'inhale', seconds: null, cue: 'Belly rises' },
    { action: 'exhale', seconds: null, cue: 'Slow, relaxed release' },
  ],
  citation: 'NAUI Freediver Manual (2021) p.34',
};

/**
 * Purge breathing. NAUI permits a small, capped number of purges; much other
 * training material forbids any hyperventilation outright. We implement NAUI's
 * version but default it off and cap it hard — see PURGE_MAX_BREATHS.
 */
export const PURGE_BREATHING: BreathingProtocol = {
  id: 'purge',
  name: 'Purge breathing',
  source: 'naui',
  purpose: 'Slightly reduce CO2 before a hold.',
  steps: [
    { action: 'inhale', seconds: 1, cue: 'Quick inhale' },
    { action: 'exhale', seconds: 4, cue: 'Fast, full exhale' },
  ],
  repetitions: { min: 3, max: 5 },
  caution:
    'Dizziness or light-headedness from hypocapnia can occur — stop if it does. '
    + 'Follow with 2-3 normal ventilation breaths before the peak inhalation. '
    + 'Never use purging to extend a hold beyond your planned limit.',
  citation: 'NAUI Freediver Manual (2021) p.35',
};

/** Hard cap on purge breaths, per the manual. The UI must not allow more. */
export const PURGE_MAX_BREATHS = 5;

/** Normal ventilation breaths required between purging and the peak inhalation. */
export const PURGE_RECOVERY_BREATHS = { min: 2, max: 3 };

/**
 * Peak inhalation — the final breath before a hold. Segmented, filling from the
 * diaphragm upward. This is a sequence of body cues rather than a timed pattern.
 */
export const PEAK_INHALATION_SEGMENTS: Array<{ segment: string; cue: string }> = [
  { segment: 'Diaphragm', cue: 'Fill low — belly first' },
  { segment: 'Intercostals', cue: 'Ribs expand outward' },
  { segment: 'Shoulders', cue: 'Upper chest fills' },
  { segment: 'Subclavians', cue: 'Shrug shoulders, extend neck to top off' },
];

export const PEAK_INHALATION_CAUTION =
  'Cerebral vasoconstriction at peak inhalation can cause a momentary blackout. '
  + 'Novice freedivers should build up to a full peak inhalation gradually. Never pack.';

/**
 * Recovery breathing. NAUI requires six breaths after EVERY hold, no exceptions —
 * "the most important breaths you will take in preventing blackout". Which six
 * depends on the discipline.
 *
 * Dry training uses the static/dynamic variant (cleansing breaths).
 */
export const CLEANSING_BREATH: BreathStep[] = [
  { action: 'inhale', seconds: 1, cue: 'In' },
  { action: 'exhale', seconds: 1, cue: 'Out' },
];

export const HOOK_BREATH: BreathStep[] = [
  { action: 'inhale', seconds: 1, cue: 'Fill the top half' },
  { action: 'hold', seconds: 3, cue: 'Hold — raises blood pressure' },
  { action: 'exhale', seconds: 1, cue: 'Release' },
];

export interface RecoveryProtocol {
  id: string;
  name: string;
  /** Applies after this kind of breath-hold. */
  appliesTo: string;
  /** Ordered breath types making up the required six. */
  breaths: Array<{ type: 'cleansing' | 'hook'; steps: BreathStep[] }>;
  citation: string;
}

/** Static and dynamic apnea: six rapid cleansing breaths. Used for all dry training. */
export const RECOVERY_STATIC: RecoveryProtocol = {
  id: 'recovery-static',
  name: 'Recovery breathing — static / dynamic',
  appliesTo: 'Static apnea, dynamic apnea, and all dry breath-hold training',
  breaths: Array.from({ length: 6 }, () => ({ type: 'cleansing' as const, steps: CLEANSING_BREATH })),
  citation: 'NAUI Freediver Manual (2021) p.35',
};

/** Depth diving: three hook breaths then three cleansing. Out of scope for dry training. */
export const RECOVERY_DEPTH: RecoveryProtocol = {
  id: 'recovery-depth',
  name: 'Recovery breathing — depth',
  appliesTo: 'Depth dives (constant ballast, free immersion)',
  breaths: [
    ...Array.from({ length: 3 }, () => ({ type: 'hook' as const, steps: HOOK_BREATH })),
    ...Array.from({ length: 3 }, () => ({ type: 'cleansing' as const, steps: CLEANSING_BREATH })),
  ],
  citation: 'NAUI Freediver Manual (2021) p.35',
};

/** Six, always. Enforced by the runner rather than offered as a preference. */
export const REQUIRED_RECOVERY_BREATHS = 6;

/**
 * NAUI: watch a diver for at least 30 seconds after they surface, because
 * roughly 90% of freediving blackouts happen after the diver is already
 * breathing again. The runner holds the user in a monitor phase for this long.
 */
export const POST_HOLD_MONITOR_SECONDS = 30;

export const POST_HOLD_MONITOR_CUE =
  'Stay upright and keep breathing. Do not close your eyes or lie back until '
  + 'breathing is fully normal — most blackouts happen after surfacing.';

/**
 * Supplementary drills. Not from the NAUI manual; they target scuba gas
 * consumption and autonomic tone rather than breath-hold performance.
 */
export const DIAPHRAGMATIC_360: BreathingProtocol = {
  id: 'diaphragmatic-360',
  name: '360° diaphragmatic breathing',
  source: 'supplementary',
  purpose: 'Rib-cage mechanics and a complete exhale — the highest-yield drill for scuba RMV.',
  steps: [
    { action: 'inhale', seconds: 4, cue: 'Lower ribs widen — front, sides, back' },
    { action: 'exhale', seconds: 6, cue: 'Empty completely' },
  ],
  durationSeconds: { min: 300, max: 600 },
  caution: 'Chest should stay nearly still. An incomplete exhale traps CO2 and raises respiratory rate.',
  citation: 'Freediver Breathing Program §E1',
};

export const EXTENDED_EXHALE: BreathingProtocol = {
  id: 'extended-exhale',
  name: 'Extended exhale (1:2)',
  source: 'supplementary',
  purpose: 'CO2 tolerance and the unhurried in / long out rhythm wanted on a regulator.',
  steps: [
    { action: 'inhale', seconds: 4, cue: 'In through the nose' },
    { action: 'exhale', seconds: 8, cue: 'Twice as long, unforced' },
  ],
  durationSeconds: { min: 300, max: 600 },
  caution: 'If 8 seconds strains, start at 4:6 and build.',
  citation: 'Freediver Breathing Program §E2',
};

export const RESONANCE_BREATHING: BreathingProtocol = {
  id: 'resonance',
  name: 'Resonance breathing (6 breaths/min)',
  source: 'supplementary',
  purpose: 'HRV and vagal tone. Lowest risk, strongest general-health evidence.',
  steps: [
    { action: 'inhale', seconds: 5, cue: 'In' },
    { action: 'exhale', seconds: 5, cue: 'Out' },
  ],
  durationSeconds: { min: 600, max: 1200 },
  caution: 'Keep it paced, not deep — roughly normal tidal volume. No holds.',
  citation: 'Freediver Breathing Program §E3',
};

export const BOX_BREATHING: BreathingProtocol = {
  id: 'box',
  name: 'Box breathing',
  source: 'supplementary',
  purpose: 'Composure and pre-dive calm. Mild CO2 stimulus only.',
  steps: [
    { action: 'inhale', seconds: 4, cue: 'In' },
    { action: 'hold', seconds: 4, cue: 'Hold' },
    { action: 'exhale', seconds: 4, cue: 'Out' },
    { action: 'holdEmpty', seconds: 4, cue: 'Hold empty' },
  ],
  durationSeconds: { min: 300, max: 600 },
  citation: 'Freediver Breathing Program §E4',
};

export const BREATHING_PROTOCOLS: BreathingProtocol[] = [
  NORMAL_BREATHING,
  VENTILATION_BREATHING,
  PURGE_BREATHING,
  DIAPHRAGMATIC_360,
  EXTENDED_EXHALE,
  RESONANCE_BREATHING,
  BOX_BREATHING,
];

export const getProtocol = (id: string): BreathingProtocol | undefined =>
  BREATHING_PROTOCOLS.find(p => p.id === id);

/** One cycle length in seconds; null if any step is user-ended. */
export const protocolCycleSeconds = (protocol: BreathingProtocol): number | null =>
  protocol.steps.some(s => s.seconds === null)
    ? null
    : protocol.steps.reduce((sum, s) => sum + (s.seconds ?? 0), 0);

/**
 * BOLT / control pause reference. Measured on waking, before rising, and stopped
 * at the FIRST definite urge to breathe — it is not a max breath-hold.
 */
export const BOLT_SCALE: Array<{ label: string; maxSeconds: number | null }> = [
  { label: 'Very poor', maxSeconds: 10 },
  { label: 'Poor', maxSeconds: 15 },
  { label: 'Moderate', maxSeconds: 25 },
  { label: 'Good', maxSeconds: 39 },
  { label: 'Excellent', maxSeconds: null },
];

export const boltRating = (seconds: number): string =>
  BOLT_SCALE.find(band => band.maxSeconds === null || seconds <= band.maxSeconds)!.label;

export const BOLT_GUIDANCE =
  'Measure on waking, before getting out of bed. Breathe normally through the nose, '
  + 'then take a small, silent breath in and out and pinch the nose. Stop at the first '
  + 'definite urge to breathe. If your first breath after is large or gasping, you held '
  + 'too long and the number is invalid.';

/** Conservative max-static estimate when no measured attempt exists yet. */
export const estimateMaxStaticFromBolt = (boltSeconds: number): number => boltSeconds * 4;
